import React, { useState, useRef, useEffect } from "react";
import { LuUserRoundCog } from "react-icons/lu";
import { IconButton } from "@vibe/core";
import { UserAvatar } from "../../../../../ui/UserAvatar";
import logger from "../../../../../../lib/logger";
import { postTransferOwnerShip } from "../../../../../../utils/requests/postTransferOwnerShip";
import { Webhook } from "../WebhooksSettings";

type WebhookStatus =
  | "installed"
  | "not_installed"
  | "reinstalling"
  | "success"
  | "error"
  | "loading";

interface WebhookState {
  status: WebhookStatus;
  error?: string;
}

interface WebhookOwnershipDropdownProps {
  webhooks: Webhook[];
  authorizedUsers: any[];
  authorizedUsersLoading: boolean;
  authorizedUsersError: boolean;
  boardId: number;
  token: string;
  revalidate: () => void;
  setWebhookStates: React.Dispatch<React.SetStateAction<Record<string, WebhookState>>>;
  disabled?: boolean;
  setSelectedWebhooks?: React.Dispatch<React.SetStateAction<Webhook[]>>;
}

const WebhookOwnershipDropdown: React.FC<WebhookOwnershipDropdownProps> = ({
  webhooks,
  authorizedUsers,
  authorizedUsersLoading,
  authorizedUsersError,
  boardId,
  token,
  revalidate,
  setWebhookStates,
  setSelectedWebhooks,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTransferOwnership = async (userId: number, accountId: number) => {
    try {
      setIsOpen(false);

      const reinstallingStates = webhooks.reduce(
        (acc, webhook) => ({
          ...acc,
          [webhook?.event]: { status: "reinstalling" as WebhookStatus },
        }),
        {},
      );
      setWebhookStates((prev) => ({ ...prev, ...reinstallingStates }));

      for (const webhook of webhooks) {
        const webhookKey = webhook.event;
        try {
          if (!webhook?.id || !webhook?.event) continue;

          await postTransferOwnerShip(
            webhook.event,
            userId.toString(),
            accountId.toString(),
            boardId,
            token,
            webhook.id,
          );

          setWebhookStates((prev) => ({
            ...prev,
            [webhookKey]: { status: "success" },
          }));

          setTimeout(() => {
            revalidate();
          }, 1000);
        } catch (error: any) {
          logger.error("Error transferring webhook", error);

          setWebhookStates((prev) => ({
            ...prev,
            [webhookKey]: { status: "error", error: error.message },
          }));

          setTimeout(() => {
            setWebhookStates((prev) => ({
              ...prev,
              [webhookKey]: { status: "installed" },
            }));
          }, 2000);
          continue;
        }
      }

      setSelectedWebhooks?.([]);
    } catch (error: any) {
      logger.error("Error transferring ownership:", error);
    } finally {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <IconButton
        icon={LuUserRoundCog}
        kind="secondary"
        size="small"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        tooltipContent="Transfer Ownership"
        disabled={disabled}
      />

      {isOpen && !disabled && (
        <div className="absolute right-0 z-50 mt-2 w-56 bg-white rounded-md border border-gray-200 shadow-lg">
          <div className="flex flex-col py-1">
            {authorizedUsersLoading ? (
              <div className="px-4 py-2 text-sm text-gray-500">Loading users...</div>
            ) : authorizedUsersError ? (
              <div className="px-4 py-2 text-sm text-red-500">Error loading users</div>
            ) : authorizedUsers.length === 0 ? (
              <div className="px-4 py-2 text-sm text-gray-500">No authorized users found</div>
            ) : (
              authorizedUsers.map((user) => (
                <div
                  key={user?.id}
                  onClick={() => handleTransferOwnership(user?.id, user?.account?.id)}
                  className="flex gap-2 items-center px-4 py-2 w-full text-sm text-left transition-colors cursor-pointer hover:bg-gray-100"
                >
                  <UserAvatar name={user?.name} photoUrl={user?.photo_thumb} size="sm" />
                  <span>{user?.name}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookOwnershipDropdown;
